import { strToU8, zipSync } from "fflate";

import { getSupabaseClient } from "../providers/supabase/supabase";

type Row = Record<string, unknown>;

const iso = (value: unknown) =>
  typeof value === "string" ? value.replace("T", " ").slice(0, 19) : "";

/**
 * One zip with everything needed to understand what broke: a readable summary
 * (report.md) plus the raw rows. Meant to be handed to the developer or to an
 * AI as is. Holds no buyer names or phones: the log and the events never
 * store them, and no lead tables are exported.
 */
export const buildSystemReport = async (viewer: string) => {
  const db = getSupabaseClient();
  const weekAgo = new Date(Date.now() - 7 * 24 * 3600_000).toISOString();
  const [health, log, events, meta, connectors, config, roles, team] =
    await Promise.all([
      db.rpc("system_health"),
      db
        .from("system_log")
        .select("*")
        .gte("at", weekAgo)
        .order("at", { ascending: false })
        .limit(1000),
      db
        .from("events")
        .select("id, occurred_at, type, sales_id, deal_id, contact_id, payload")
        .order("occurred_at", { ascending: false })
        .limit(300),
      db.rpc("meta_connector_status"),
      db.from("connector_settings").select("id, value, updated_at"),
      db.from("configuration").select("config").limit(1),
      db.from("roles").select("id, key, name, permissions, locked"),
      db.from("sales").select("id, first_name, last_name, role_id, disabled"),
    ]);

  const logRows = (log.data ?? []) as Row[];
  const groups = new Map<string, { count: number; last: string }>();
  for (const row of logRows) {
    const key = `[${row.level}] ${row.source}: ${row.message}`;
    const group = groups.get(key) ?? { count: 0, last: String(row.at) };
    group.count++;
    groups.set(key, group);
  }
  const h = (health.data ?? {}) as Row & {
    cron?: {
      job: string;
      last_status: string;
      last_run: string;
      last_error: string;
    }[];
  };

  const md = [
    `# Oselya CRM: system report`,
    ``,
    `- Generated: ${new Date().toISOString()}`,
    `- App: ${window.location.origin} (version ${import.meta.env.VITE_APP_VERSION ?? "dev"})`,
    `- Downloaded by: ${viewer}`,
    `- Browser: ${navigator.userAgent}`,
    ``,
    `## Health`,
    `- Errors in 24 h: ${h.errors_24h ?? "?"}`,
    `- Last lead: ${iso(h.last_lead_at) || "—"}, leads today: ${h.leads_today ?? "?"}`,
    `- Telegram sent in 24 h: ${h.telegram_sent_24h ?? "?"}, queued: ${h.telegram_queue ?? "?"}`,
    `- Active users: ${h.users ?? "?"}`,
    ``,
    `## Background jobs`,
    ...(h.cron ?? []).map(
      (j) =>
        `- ${j.job}: ${j.last_status ?? "never ran"} at ${iso(j.last_run) || "—"}${j.last_error ? ` — ${j.last_error}` : ""}`,
    ),
    ``,
    `## Errors of the last 7 days, grouped (${logRows.length} lines)`,
    ...(groups.size
      ? [...groups.entries()]
          .sort((a, b) => b[1].count - a[1].count)
          .map(([key, g]) => `- ${g.count}× ${key} (last ${iso(g.last)})`)
      : ["- none"]),
    ``,
    `## Last 30 errors in full`,
    ...logRows
      .slice(0, 30)
      .flatMap((row) => [
        ``,
        `### ${iso(row.at)} [${row.level}] ${row.source}`,
        String(row.message),
        "```json",
        JSON.stringify(row.details ?? {}, null, 2),
        "```",
      ]),
    ``,
    `## Files`,
    `- system_log.json: every log line of 7 days`,
    `- events.json: the last 300 actions in the CRM (ids only)`,
    `- health.json, meta_status.json, connectors.json, configuration.json, roles.json, team.json`,
    ``,
    `Fetch errors while building this report: ${
      [health, log, events, meta, connectors, config, roles, team]
        .map((r) => r.error?.message)
        .filter(Boolean)
        .join("; ") || "none"
    }`,
  ].join("\n");

  const json = (value: unknown) =>
    strToU8(JSON.stringify(value ?? null, null, 2));
  const zip = zipSync({
    "report.md": strToU8(md),
    "system_log.json": json(log.data),
    "events.json": json(events.data),
    "health.json": json(health.data),
    "meta_status.json": json(meta.data),
    "connectors.json": json(connectors.data),
    "configuration.json": json(config.data?.[0]?.config),
    "roles.json": json(roles.data),
    "team.json": json(team.data),
  });

  const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
  const host = window.location.hostname.split(".")[0];
  const url = URL.createObjectURL(new Blob([zip], { type: "application/zip" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `oselya-report-${host}-${stamp}.zip`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};
